import {
  createApi,
  createEffect,
  createEvent,
  createStore,
  sample,
} from "effector";
import { persist } from "effector-storage/session";
import { persist as persistLocal } from "effector-storage/local";

import { Account } from "@hunkware/shared/types";
import {
  accountCreate,
  accountUpdate,
  leadMetaUpsert,
  leadUpdate,
  markLeadInProgress,
} from "../../shared/api";

import { AccountStore, AccountBasicStore } from "./types";
import { defaultBasicState, defaultState } from "./utils";
import { serviceCheckFx, $serviceLocation } from "../locations";

/** Identity of a saved item volume — a new lead or a new code both count as a change. */
export const leadVolumeKey = (leadId: number, code: string): string =>
  `${leadId}:${code}`;

export const $account = createStore<AccountStore>(defaultState);

persist<AccountStore>({
  store: $account,
  key: "booking-v4-account",
  def: defaultState,
});

export const $accountBasic = createStore<AccountBasicStore>(defaultBasicState);

persistLocal<AccountBasicStore>({
  store: $accountBasic,
  key: "booking-v4-account",
  def: defaultBasicState,
});

// Sync billingAddress and contact info from $account to $accountBasic
$accountBasic.on($account, (state, accountStore) => {
  if (!accountStore.account) {
    return state;
  }

  const { account } = accountStore;

  return {
    firstName: account.firstName || state.firstName,
    lastName: account.lastName || state.lastName,
    phone: account.phone || state.phone,
    email: account.email || state.email,
    billingAddress: account.billingAddress || state.billingAddress,
  };
});

export const accountCreateFx = createEffect(accountCreate);
$account
  .on(accountCreateFx.done, (state, response) => {
    const serverAccount = response.result.data.tokens[0].account;
    return {
      ...state,
      account: {
        ...state.account,
        ...serverAccount,
        // Protect locally-stored nested objects from null/undefined in server response.
        // The server may return null for fields not yet persisted (e.g. billingAddress
        // before createAddress runs), but we must not overwrite locally-set values.
        billingAddress:
          serverAccount.billingAddress ?? state.account?.billingAddress,
        // Normalize category.id to a number — the PHP API may return it as a string
        // (e.g. "5"). Keeping it as a number prevents strict-equality mismatches
        // in useDataFromParams that would erroneously trigger a full store reset.
        category: serverAccount.category
          ? {
              ...serverAccount.category,
              id: Number(serverAccount.category.id),
            }
          : state.account?.category,
        type: serverAccount.type ?? state.account?.type,
      },
      token: response.result.data.tokens[0].token,
      lead: response.result.data.tokens[0].lead ?? state.lead,
    };
  })
  .on(accountCreateFx.pending, (state, loading) => ({ ...state, loading }));

export const accountUpdateFx = createEffect(accountUpdate);
$account
  .on(accountUpdateFx.done, (state, response) => {
    const serverAccount = response.result.data.accounts[0];
    return {
      ...state,
      account: {
        ...state.account,
        ...serverAccount,
        billingAddress:
          serverAccount.billingAddress ?? state.account?.billingAddress,
        // Normalize category.id to a number — see accountCreateFx.done comment.
        category: serverAccount.category
          ? {
              ...serverAccount.category,
              id: Number(serverAccount.category.id),
            }
          : state.account?.category,
        type: serverAccount.type ?? state.account?.type,
      },
      lead: response.result.data.lead ?? state.lead,
    };
  })
  .on(accountUpdateFx.pending, (state, loading) => ({ ...state, loading }));

export const leadUpdateFx = createEffect(leadUpdate);

// No `.on(...)` handler: unlike leadUpdate, this call sends no strings and reads nothing
// back that the funnel still needs, so there is no store state to keep in sync.
export const markLeadInProgressFx = createEffect(markLeadInProgress);

// CID-7928 — booking answers saved on the lead (account_lead_meta), analytics only.
export const leadMetaUpsertFx = createEffect(leadMetaUpsert);

/**
 * `<leadId>:<code>` of the last item volume the server confirmed. The contract that sends it
 * re-runs on every later step, so this is what keeps it to one request per actual change. Set
 * only on success: a failed save is retried on the next step. Not persisted — after a reload the
 * worst case is one repeat request, which the server treats as a no-op.
 */
export const $leadVolumeSaved = createStore<string | null>(null).on(
  leadMetaUpsertFx.done,
  (state, { params }) =>
    params.payload.junkItemVolume === undefined
      ? state
      : leadVolumeKey(params.leadId, params.payload.junkItemVolume),
);

export const accountApi = createApi($account, {
  set: (prevState: AccountStore, account: Partial<Account>): AccountStore => {
    return {
      ...prevState,
      account: {
        ...prevState.account,
        ...account,
      },
    };
  },

  setAddress: (
    prevState: AccountStore,
    address: Account["billingAddress"],
  ) => ({
    ...prevState,
    account: {
      ...prevState.account,
      billingAddress: address,
    },
  }),

  reset: () => defaultState,
});

// Hydrate $account from $accountBasic on startup.
// When the user closes the tab and reopens, $account (session storage) is empty
// but $accountBasic (localStorage) preserves contact info and address.
const basicState = $accountBasic.getState();
const accountState = $account.getState();
if (
  !accountState.account?.firstName &&
  !accountState.account?.email &&
  !accountState.account?.billingAddress?.address &&
  (basicState.firstName ||
    basicState.email ||
    basicState.billingAddress?.address)
) {
  accountApi.set({
    firstName: basicState.firstName,
    lastName: basicState.lastName,
    phone: basicState.phone,
    email: basicState.email,
    billingAddress: basicState.billingAddress,
  });
}

// When serviceCheck returns a different location from where the account was
// created, reset account.id and token so accountCreate re-fires via
// tokens/generate at the new location (do NOT use PUT / accountUpdate).
export const resetAccountForLocationChange = createEvent();
$account.on(resetAccountForLocationChange, (state) => ({
  ...state,
  account: {
    ...state.account,
    id: undefined,
    location: undefined,
  },
  token: undefined,
}));

sample({
  clock: serviceCheckFx.done,
  source: { account: $account, serviceLocation: $serviceLocation },
  filter: ({ account, serviceLocation }) => {
    const accountLocationId = account.account?.location?.id;
    const newLocationId = serviceLocation.location?.id;
    return (
      !!account.account?.id &&
      !!accountLocationId &&
      !!newLocationId &&
      accountLocationId !== newLocationId
    );
  },
  target: resetAccountForLocationChange,
});
